import { Check, Loader2 } from "lucide-react";
import type { InboxViewRun } from "@/types/inboxView/inboxView";
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";

interface Props {
  status: InboxViewRun["status"] | "designing" | "extracting" | "pending";
  message?: string | null;
  transcriptsAttempted?: number;
  transcriptsExtracted?: number;
  columnNames?: string[];
}

const STEPS = [
  { key: "designing",  label: "Designing the columns" },
  { key: "extracting", label: "Reading transcripts and filling in rows" },
  { key: "complete",   label: "Your view is ready" },
];

const order = ["pending", "designing", "extracting", "complete"];

export const ThinkingPanel = ({ status, message, transcriptsAttempted, transcriptsExtracted, columnNames }: Props) => {
  const currentIdx = order.indexOf(status);
  const isComplete = status === "complete";

  return (
    <div className="p-[2px] rounded-2xl bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] hg-thinking-panel">
      <div className="bg-white rounded-[14px] p-5">
        <div className="flex items-start gap-4">
          <div
            className="h-16 w-16 rounded-2xl bg-gradient-to-br from-[#4E9CFF] to-[#FF4DB8] flex items-center justify-center shrink-0"
          >
            <GenieIcon
              name="sparkles"
              size={36}
              color="#ffffff"
              label="Help Genie"
            />
          </div>

          <div className="flex-1">
            {isComplete ? (
              <div>
                <div className="text-base font-bold text-[#222B45]">Your view is ready</div>
                <div className="text-xs text-[#64748B] mt-0.5">
                  {transcriptsExtracted && transcriptsExtracted > 0
                    ? `${transcriptsExtracted} ${transcriptsExtracted === 1 ? "row" : "rows"} brought in from your conversations.`
                    : "Your Genies are all caught up."}
                </div>
              </div>
            ) : (
              <div className="text-xs font-semibold uppercase tracking-wider text-[#64748B] mb-3">Building your view</div>
            )}

            {!isComplete && (
              <div className="space-y-2">
                {STEPS.map((step) => {
                  const idx = order.indexOf(step.key);
                  const done = idx < currentIdx;
                  const active = idx === currentIdx;
                  return (
                    <div key={step.key} className="flex items-center gap-2.5 text-sm">
                      {done ? (
                        <Check className="h-4 w-4" strokeWidth={3} style={{ color: "#1FAA59" }} />
                      ) : active ? (
                        <Loader2 className="h-4 w-4 animate-spin text-genie-primary" />
                      ) : (
                        <span className="h-4 w-4 rounded-full border-2 border-[#E5EAF0]" />
                      )}
                      <span className={active ? "text-[#222B45] font-medium" : done ? "text-[#64748B]" : "text-[#B6C0CC]"}>
                        {step.label}
                      </span>
                      {active && transcriptsAttempted && transcriptsAttempted > 0 && (
                        <span className="text-xs text-[#64748B] ml-2">
                          {transcriptsExtracted ?? 0} / {transcriptsAttempted}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {columnNames && columnNames.length > 0 && (
              <div className="mt-4">
                <div className="text-xs font-semibold uppercase tracking-wider text-[#64748B] mb-2">Columns</div>
                <div className="flex flex-wrap gap-1.5">
                  {columnNames.map((c, i) => (
                    <span
                      key={c}
                      className="text-xs font-semibold px-2.5 py-1 rounded-full bg-genie-primary/10 text-genie-primary hg-column-pop"
                      style={{ animationDelay: `${i * 60}ms` }}
                    >
                      {c}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {message && <div className="mt-3 text-xs text-[#64748B]">{message}</div>}
          </div>
        </div>
      </div>

      <style>{`
        .hg-thinking-panel {
          animation: hgPanelFadeIn 280ms ease-out both;
        }
        @keyframes hgPanelFadeIn {
          from { opacity: 0; transform: translateY(-4px); }
          to   { opacity: 1; transform: translateY(0); }
        }

        .hg-column-pop {
          animation: hgColumnPop 400ms cubic-bezier(.34,1.56,.64,1) both;
        }
        @keyframes hgColumnPop {
          from { opacity: 0; transform: translateY(6px) scale(.9); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>
    </div>
  );
};
